import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Instagram, MessageCircle, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const ESTABLISHMENT_TYPES = [
  "Padaria / Loja de bairro",
  "Supermercado de pequeno porte",
  "Supermercado de médio porte",
  "Supermercado de grande porte",
  "Atacadista / Atacarejo",
  "Hipermercado",
  "Outro",
];

const INITIAL = {
  nome: "",
  empresa: "",
  telefone: "",
  email: "",
  tipo_estabelecimento: "",
  quantidade_carrinhos: "",
  mensagem: "",
};

const inputCls =
  "w-full rounded-xl border border-white/10 bg-[#1E3A8A] px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-colors duration-200 focus:border-[#FF5722]";

export const Contact = () => {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        quantidade_carrinhos: form.quantidade_carrinhos || null,
        mensagem: form.mensagem || null,
      });
      toast.success("Solicitação enviada!", {
        description: "Nossa equipe entrará em contato em breve.",
      });
      setForm(INITIAL);
    } catch (err) {
      toast.error("Não foi possível enviar.", {
        description: "Tente novamente ou fale conosco pelo WhatsApp.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contato" className="relative py-24 sm:py-32" data-testid="contact-section">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[24rem] w-[44rem] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(255,87,34,0.1)_0%,rgba(30,58,138,0)_70%)]" />
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: EASE }}
          >
            <p className="font-tech text-xs font-bold uppercase tracking-[0.25em] text-[#A3E635]">Contato</p>
            <h2 className="font-display mt-4 text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">
              Transforme reposição em <span className="text-[#A3E635]">economia real.</span>
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-slate-400">
              Conte com uma equipe especializada em prevenção de perdas. Solicite uma proposta sem compromisso para o
              seu estabelecimento.
            </p>

            <div className="mt-10 space-y-4">
              <a
                href="https://wa.me/5531992284075"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-card"
                className="group flex items-center gap-4 rounded-2xl border border-[#10B981]/30 bg-[#1B3579] p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#10B981]/60"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#10B981]/15 text-[#10B981]">
                  <MessageCircle className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-tech text-[10px] uppercase tracking-[0.2em] text-slate-400">WhatsApp direto</p>
                  <p className="font-display text-lg font-bold">(31) 99228-4075</p>
                </div>
              </a>
              <a
                href="mailto:resgatecaroficial@gmail.com"
                data-testid="contact-email-card"
                className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-[#1B3579] p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#06B6D4]/50"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#06B6D4]/15 text-[#06B6D4]">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-tech text-[10px] uppercase tracking-[0.2em] text-slate-400">E-mail</p>
                  <p className="font-display text-lg font-bold break-all">resgatecaroficial@gmail.com</p>
                </div>
              </a>
              <a
                href="https://instagram.com/resgatecaroficial"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-instagram-card"
                className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-[#1B3579] p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#FF5722]/50"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#FF5722]/15 text-[#A3E635]">
                  <Instagram className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-tech text-[10px] uppercase tracking-[0.2em] text-slate-400">Instagram</p>
                  <p className="font-display text-lg font-bold">@resgatecaroficial</p>
                </div>
              </a>
            </div>
          </motion.div>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.12, ease: EASE }}
            className="rounded-2xl border border-white/10 bg-[#1B3579] p-7 sm:p-9"
            data-testid="contact-form"
          >
            <h3 className="font-display text-xl font-bold tracking-tight">Solicite uma proposta</h3>
            <p className="mt-1 text-sm text-slate-400">Resposta rápida, sem compromisso.</p>

            <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <input required placeholder="Seu nome*" value={form.nome} onChange={set("nome")} className={inputCls} data-testid="form-input-nome" />
              <input required placeholder="Empresa*" value={form.empresa} onChange={set("empresa")} className={inputCls} data-testid="form-input-empresa" />
              <input required placeholder="Telefone / WhatsApp*" value={form.telefone} onChange={set("telefone")} className={inputCls} data-testid="form-input-telefone" />
              <input required type="email" placeholder="E-mail*" value={form.email} onChange={set("email")} className={inputCls} data-testid="form-input-email" />
              <select
                required
                value={form.tipo_estabelecimento}
                onChange={set("tipo_estabelecimento")}
                className={`${inputCls} ${form.tipo_estabelecimento ? "" : "text-slate-600"}`}
                data-testid="form-select-tipo"
              >
                <option value="" disabled>
                  Tipo de estabelecimento*
                </option>
                {ESTABLISHMENT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <input
                placeholder="Qtd. aproximada de carrinhos"
                value={form.quantidade_carrinhos}
                onChange={set("quantidade_carrinhos")}
                className={inputCls}
                data-testid="form-input-carrinhos"
              />
              <textarea
                rows={4}
                placeholder="Conte um pouco sobre a sua operação..."
                value={form.mensagem}
                onChange={set("mensagem")}
                className={`${inputCls} resize-none sm:col-span-2`}
                data-testid="form-input-mensagem"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="form-submit-button"
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[#FF5722] px-6 py-4 font-tech text-xs font-bold uppercase tracking-[0.15em] text-[#1E3A8A] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#E64A19] hover:shadow-[0_12px_40px_rgba(255,87,34,0.4)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {loading ? "Enviando..." : "Enviar solicitação"}
            </button>
            <p className="mt-4 flex items-center justify-center gap-2 text-center font-tech text-[10px] uppercase tracking-[0.18em] text-slate-500">
              <Phone className="h-3 w-3" /> Atendimento em todo o Brasil
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
